import React from 'react';
import { Button } from 'react-bootstrap';
import { getAllServices } from '../../sources/service';
import FlowSource from '../../sources/flow';
import { isNil, assocPath } from 'ramda';
import { bindMethods } from '../../utils/core';
import Restrictions from './Restrictions';
import GroupOrUserSection from './GroupOrUserSection';
import VersionSection from './VersionSection';
import '../../../css/flow/properties.less';
import { addNotification } from '../../actions/reduxActions/notification';
import { fetchAllFlows } from '../../actions/reduxActions/flows';
import { connect } from 'react-redux';
import {
    registerFlowConfiguration,
    unregisterFlowConfiguration,
    changeFlowName,
    changeFlowAccess,
    changeFlowSocialFeed,
    changeFlowHistoricalNavigation,
    changeFlowJumping,
    changeFlowStateLifetime,
    changeFlowIdleStateLifetime,
    changeFlowComment,
    changeFlowService,
    setFlowUsersOrGroups,
    addFlowUsersOrGroups,
    changeFlowUserAttribute,
    deleteFlowUser,
    changeFlowGroupAttribute,
    deleteFlowGroup,
} from '../../actions/reduxActions/flowConfigurations';
import EditorActions from '../../actions/editor';

const initialState = {
    // This is basic info about all the Services in the Tenant
    // Used to get service names and ids to select from
    isLoadingServiceData: true,
    serviceData: null,
    // This is all the groups from the Draw 2 service
    // Used to get all of the groups the service supports
    isLoadingServiceGroupData: true,
    serviceGroupData: null,
    // This is all group attributes from the Draw 2 service
    // Used to get all of the group attributes the service supports
    isLoadingServiceGroupAttributeData: true,
    serviceGroupAttributeData: null,
    // This is all the users from the Draw 2 service
    // Used to get all of the users the service supports
    isLoadingServiceUserData: true,
    serviceUserData: null,
    // This is all user attributes from the Draw 2 service
    // Used to get all of the user attributes the service supports
    isLoadingServiceUserAttributeData: true,
    serviceUserAttributeData: null,
};

export class Properties extends React.Component {

    constructor() {
        super();

        this.state = initialState;

        bindMethods(
            this,
            'handleEscKey',
            'saveChanges',
        );
    }

    componentDidMount() {
        this.setState({
            isLoadingBasicData: true,
        });
        // Get basic Flow information
        FlowSource.getLatest(this.props.flowId, this.props.tenantId, this.props.authenticationToken)
            .then(
                (flowData) => {
                    this.setState({ isLoadingBasicData: false });
                    this.props.registerFlowConfiguration(flowData);
                },
            ).catch(
                error => this.props.addNotification({
                    type: 'error',
                    message: error.message,
                    isPersistent: true,
                }),
            );

        // Get all Services
        getAllServices({
            token: this.props.authenticationToken,
            tenantId: this.props.tenantId,
        }).then(
            serviceData => this.setState({
                serviceData,
                isLoadingServiceData: false,
            }),
        ).catch(
            error => this.props.addNotification({
                type: 'error',
                message: error.message,
                isPersistent: true,
            }),
        );

        document.addEventListener('keydown', this.handleEscKey);
    }

    componentWillUnmount() {
        this.props.unregisterFlowConfiguration(this.props.flowId);
        document.removeEventListener('keydown', this.handleEscKey);
    }

    handleEscKey({ keyCode }) {
        if (keyCode === 27) {
            this.props.close();
        }
    }

    changeFlowService({ target }) {
        this.props.changeFlowService({ flowId: this.props.flowId, flowService: target.value });
    }

    saveChanges() {
        let { flowData } = this.props;

        // If the Flow has been set to not use Users and Groups
        if (flowData.authorization.globalAuthenticationType !== 'SPECIFIED') {
            // Then remove the users and groups
            flowData = assocPath(['authorization', 'users'], null, flowData);
            flowData = assocPath(['authorization', 'groups'], null, flowData);
        }

        if (
            (
                // If there are groups
                flowData.authorization.groups &&
                (
                    // And the groups are wrong
                    flowData.authorization.groups.some(item => item.authenticationId === -1) ||
                    flowData.authorization.groups.some(item => item.attribute === -1)
                )
            ) ||
            (
                // If there are users
                flowData.authorization.users &&
                (
                    // And the users are wrong
                    flowData.authorization.users.some(item => item.authenticationId === -1) ||
                    flowData.authorization.users.some(item => item.attribute === -1)
                )
            )
        ) {
            // Catch unfilled forms and notify
            this.props.addNotification({
                type: 'error',
                message: 'Please select items from all of the dropdowns',
                isPersistent: true,
            });
            return;
        }

        FlowSource.setLatest(
            flowData,
            this.props.tenantId,
            this.props.authenticationToken,
        ).then(() => {
            EditorActions.setEditorNameAndType(this.props.editorId, flowData.developerName, 'flow');
            this.props.close();
            this.props.addNotification({
                type: 'success',
                message: 'Flow Properties successfully saved',
                isPersistent: false,
            });
            // fetch flows
            this.props.fetchAllFlows(
                this.props.tenantId,
                this.props.authenticationToken,
            );
        }).catch((error) => {
            // Catch error and notify
            this.props.addNotification({
                type: 'error',
                message: error.message,
                isPersistent: true,
            });
        });
    }

    renderLoadingSection = () => React.createElement(manywho.component.getByName('wait'), { isVisible: true, message: 'Loading flow details' }, null)

    renderPropertiesSection() {
        const nameArea = (
            <div className="form-group">
                <label>Name</label>
                <input
                    maxLength={150}
                    className="form-control name"
                    value={this.props.flowData.developerName}
                    onChange={({ target }) => {
                        this.props.changeFlowName(
                            { flowId: this.props.flowId, flowName: target.value },
                        );
                    }}
                />
            </div>
        );

        const commentsArea = (
            <React.Fragment>
                <label>Comments</label>
                <textarea
                    maxLength={255}
                    className="form-control comments"
                    rows="5"
                    value={this.props.flowData.developerSummary}
                    onChange={({ target }) => {
                        this.props.changeFlowComment(
                            { flowId: this.props.flowId, flowComment: target.value },
                        );
                    }}
                />
            </React.Fragment>
        );

        const serviceArea = (
            <div className="form-group">
                <h2>Service</h2>
                <label>Authentication provided by Service</label>
                <br />
                <select
                    onChange={e => this.changeFlowService(e)}
                    className="form-select"
                    defaultValue={this.props.flowData.authorization.serviceElementId}
                >
                    <option key="-1" value="">No Authentication Service</option>
                    {
                        this.state.serviceData ?
                            this.state.serviceData.map(service => (
                                <option
                                    key={service.id}
                                    value={service.id}
                                >
                                    {service.developerName}
                                </option>
                            ))
                            : null
                    }
                </select>
                <br />
            </div>
        );

        const accessArea = (
            <div className="form-group">
                <label>Access</label>
                <br />
                <select
                    onChange={({ target }) => {
                        this.props.changeFlowAccess(
                            { flowId: this.props.flowId, flowAccess: target.value },
                        );
                    }}
                    className="form-select"
                    value={this.props.flowData.authorization.globalAuthenticationType}
                >
                    <option
                        key="PUBLIC"
                        value="PUBLIC"
                    >
                        Anyone can run this flow
                    </option>
                    <option
                        key="ALL_USERS"
                        value="ALL_USERS"
                    >
                        Any user that can login with the selected identity service can run this flow
                    </option>
                    <option
                        key="SPECIFIED"
                        value="SPECIFIED"
                    >
                        Create restrictions for specific users and groups of users
                    </option>
                </select>
                <br />
            </div>
        );

        const groupArea = (
            <GroupOrUserSection
                textGroupOrUser="group"
                elementId={this.props.flowId}
                tenantId={this.props.tenantId}
                authenticationToken={this.props.authenticationToken}
                serviceElementId={this.props.flowData.authorization.serviceElementId}
                authorizationElement={this.props.flowData}
                setUsersOrGroups={this.props.setUsersOrGroups}
                addUsersOrGroups={this.props.addUsersOrGroups}
                deleteGroup={this.props.deleteGroup}
                updateGroupAttribute={this.props.updateGroupAttribute}
                deleteUser={this.props.deleteUser}
                updateUserAttribute={this.props.updateUserAttribute}
            />
        );

        const userArea = (
            <GroupOrUserSection
                textGroupOrUser="user"
                elementId={this.props.flowId}
                tenantId={this.props.tenantId}
                authenticationToken={this.props.authenticationToken}
                serviceElementId={this.props.flowData.authorization.serviceElementId}
                authorizationElement={this.props.flowData}
                setUsersOrGroups={this.props.setUsersOrGroups}
                addUsersOrGroups={this.props.addUsersOrGroups}
                deleteGroup={this.props.deleteGroup}
                updateGroupAttribute={this.props.updateGroupAttribute}
                deleteUser={this.props.deleteUser}
                updateUserAttribute={this.props.updateUserAttribute}
            />
        );

        const navigationArea = (
            <React.Fragment>
                <h2>Navigation</h2>
                <label>
                    Enable historical breadcrumb navigation
                    <input
                        className="checkbox-margin-left"
                        type="checkbox"
                        checked={this.props.flowData.enableHistoricalNavigation}
                        onChange={({ target }) => {
                            this.props.changeFlowHistoricalNavigation(
                                { flowId: this.props.flowId, flowHistoricalNavigation: target.checked },
                            );
                        }}
                    />
                </label>
                <br />
                <label>
                    Allow the user to move to any Map Element location in the Flow
                    <input
                        className="checkbox-margin-left"
                        type="checkbox"
                        checked={this.props.flowData.allowJumping}
                        onChange={({ target }) => {
                            this.props.changeFlowJumping(
                                { flowId: this.props.flowId, flowJumping: target.checked },
                            );
                        }}
                    />
                </label>
                <br />
            </React.Fragment>
        );

        const socialFeedArea = (
            <React.Fragment>
                <h2>Social Feed</h2>
                <label>Social feed for collaboration</label>
                <br />
                <select
                    onChange={({ target }) => {
                        this.props.changeFlowSocialFeed(
                            { flowId: this.props.flowId, flowSocialFeed: target.value },
                        );
                    }}
                    className="form-select"
                    value={this.props.flowData.authorization.streamBehaviourType}
                >
                    <option
                        key="NONE"
                        value="NONE"
                    >
                        None
                    </option>
                    <option
                        key="USE_EXISTING"
                        value="USE_EXISTING"
                    >
                        Display a social feed
                    </option>
                </select>
                <br />
            </React.Fragment>
        );

        const restrictionsArea = (
            <React.Fragment>
                <h2>Restrictions</h2>
                <div className="flex">
                    <Restrictions flowId={this.props.flowId} />
                </div>
            </React.Fragment>
        );

        const stateExpirationLengthArea = (
            <React.Fragment>
                <h2>State</h2>
                <label>Number of days before the in-flight States should be deleted</label>
                <input
                    className="smaller-form-control"
                    type="number"
                    min={0}
                    max={180}
                    placeholder="Never"
                    value={this.props.flowData.stateExpirationLength === 0 ? null : this.props.flowData.stateExpirationLength}
                    onChange={({ target }) => {
                        this.props.changeFlowStateLifetime(
                            { flowId: this.props.flowId, flowStateLifetime: target.value },
                        );
                    }}
                />
                <br />
                <label>Number of days of inactivity before the in-flight States should be deleted</label>
                <input
                    className="smaller-form-control"
                    type="number"
                    min={0}
                    max={180}
                    placeholder="Never"
                    value={this.props.flowData.idleStateExpirationLength === 0 ? null : this.props.flowData.idleStateExpirationLength}
                    onChange={({ target }) => {
                        this.props.changeFlowIdleStateLifetime(
                            { flowId: this.props.flowId, flowIdleStateLifetime: target.value },
                        );
                    }}
                />
            </React.Fragment>
        );

        const stateVersionArea = (
            <VersionSection
                flowId={this.props.flowId}
                tenantId={this.props.tenantId}
                addNotification={this.props.addNotification}
                authenticationToken={this.props.authenticationToken}
            />
        );

        const showGroupsAndUsers = this.props.flowData.authorization.globalAuthenticationType === 'SPECIFIED';

        return (
            <React.Fragment>
                {nameArea}
                {commentsArea}
                {serviceArea}
                {this.props.flowData.authorization.serviceElementId ? accessArea : null}
                <div className="authorization-config">
                    {showGroupsAndUsers ? groupArea : null}
                    {showGroupsAndUsers ? userArea : null}
                </div>
                {navigationArea}
                {socialFeedArea}
                {restrictionsArea}
                {stateExpirationLengthArea}
                {stateVersionArea}
            </React.Fragment>
        );
    }

    render() {
        const stillLoading = isNil(this.props.flowData) || this.state.isLoadingBasicData || this.state.isLoadingServiceData;

        return (
            <div className="flow-modal-standalone">
                <div className="modal-backdrop full-height" />
                <div className="modal show">
                    <div className="modal-dialog">
                        <div className="modal-content flow-properties-page">
                            <div>
                                <div className="modal-header">
                                    <h4 className="modal-title">Flow Properties</h4>
                                </div>
                                <div className="modal-body">
                                    {stillLoading ? this.renderLoadingSection() : this.renderPropertiesSection()}
                                </div>
                                <div className="modal-footer">
                                    <Button className="outcome-margin-right btn btn-default" onClick={this.props.close}>
                                    Cancel
                                    </Button>
                                    <Button className="btn btn-primary" disabled={stillLoading} onClick={this.saveChanges}>
                                    Save Flow
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

// Take in Redux state and save as local props
const mapStateToProps = (state, ownProps) => ({ flowData: state.flowConfigurations.find(flow => flow.id.id === ownProps.flowId) });

const mapDispatchToProps = {
    registerFlowConfiguration,
    unregisterFlowConfiguration,
    changeFlowName,
    changeFlowAccess,
    changeFlowSocialFeed,
    changeFlowHistoricalNavigation,
    changeFlowJumping,
    changeFlowStateLifetime,
    changeFlowIdleStateLifetime,
    changeFlowComment,
    changeFlowService,
    addNotification,
    fetchAllFlows,
    setUsersOrGroups: setFlowUsersOrGroups,
    addUsersOrGroups: addFlowUsersOrGroups,
    updateUserAttribute: changeFlowUserAttribute,
    deleteUser: deleteFlowUser,
    updateGroupAttribute: changeFlowGroupAttribute,
    deleteGroup: deleteFlowGroup,
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(Properties);
